import { DecimalPipe, NgClass } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  HostBinding,
  Inject,
  Injector,
  NgZone,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AiFeaturesService, AIStore, IQBoostViewStore } from '@maximizer/ai/data-access';
import {
  AIFeatures,
  ConfigurationService,
  ContextService,
  FeatureService,
  SessionService,
} from '@maximizer/core/shared/data-access';
import {
  CDN_URL,
  ConfigurationSetting,
  ConfigurationSettingCodes,
} from '@maximizer/core/shared/domain';
import {
  ActionEventType,
  ShowDialogArgs,
} from '@maximizer/core/shared/ui/config';
import { EmbeddedComponent } from '@maximizer/core/shared/ui/components/base';
import { UI_PIPES } from '@maximizer/core/shared/ui/pipes';
import { isNullOrEmpty } from '@maximizer/shared/util';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_SWITCH } from '@progress/kendo-angular-inputs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import {
  AiHubEventTracker,
  EventAction,
  EventJourney,
} from '../classes/event-tracker';
import { IQBoostInternalCommand } from '../classes/iqboost';
import { Labels } from '../classes/labels';

interface AiFeatureItem {
  enabled: boolean;
  enabledAdmin: boolean;
  title: string;
  description: string;
  location: string;
  data: string;
  aiFeaturePropertyEnabled?: keyof AIFeatures;
  app?: AiFeatureApp;
  featureFlagId?: string;
  featureFlagDefault?: boolean;
  featureFlagOrganizationId?: string;
  imgSrc?: string;
  limitation?: string;
  iconClass?: string;
}

interface AiFeatureApp {
  title: string;
  tag: string;
  featureFlagId?: string;
  args?: Record<string, string>;
}

interface AiFeatureGroupItem {
  items: AiFeatureItem[];
  category?: string;
}

interface AiHubUserData {
  dataIsSecureExpanded: boolean;
}

@Component({
  selector: 'ai-hub',
  templateUrl: './ai-hub.component.html',
  styleUrls: ['./ai-hub.component.scss'],
  providers: [AiHubEventTracker],
  imports: [
    DecimalPipe,
    FormsModule,
    KENDO_SWITCH,
    KENDO_BUTTON,
    NgClass,
    TranslatePipe,
    UI_PIPES,
  ],
})
export class AiHubComponent extends EmbeddedComponent {
  @HostBinding('class') hostClasses = 'ai-ng';

  static readonly aiHubUserDataDefault: AiHubUserData = {
    dataIsSecureExpanded: false,
  };

  readonly aiHubUserDataSettingsCodes: ConfigurationSettingCodes = {
    code3: 'AI_USER_DATA',
    code4: 'HUB_USER_DATA',
  };

  private _labels?: Labels;
  private readonly saveUserData$ = new Subject<AiHubUserData>();

  aiHubUserDataSetting: ConfigurationSetting | null = null;
  hasInitialized = false;

  loading = signal(false);
  isAiEnabled = signal(false);
  isAnonymizerEnabled = signal(false);
  isAnonymizerEnabledByAdmin = signal(false);
  isFFAnonymizerEnabled = signal(true);
  aiFeaturesGroup: WritableSignal<AiFeatureGroupItem[]> = signal([]);
  noteDataIsSecureExpanded = signal(false);
  isAdmin = signal(false);

  creditsProgressPercentage = computed(() => {
    const used = this.aiStore.aiUserDataGroup().admin.creditsUsed;
    const total = this.aiStore.aiUserDataGroup().admin.creditsTotal;
    const percentage = total > 0 ? (used / total) * 100 : 0;
    return percentage;
  });
  isSLE = computed(() => this.aiFeaturesService.isSLE());
  creditsUsed = computed(
    () => this.aiStore.aiUserDataGroup().admin.creditsUsed,
  );
  creditsTotal = computed(
    () => this.aiStore.aiUserDataGroup().admin.creditsTotal,
  );
  loadingGifUrl = computed(
    () => `${this.context.integration?.url ?? ''}/resources/ai-loading.gif`,
  );
  isAnonymizerToggleDisabled = computed(
    () =>
      !this.isFFAnonymizerEnabled() ||
      !this.isAiEnabled() ||
      (!this.isAdmin() && this.isAnonymizerEnabledByAdmin()) ||
      this.loading(),
  );

  constructor(
    element: ElementRef,
    private readonly ngZone: NgZone,
    private readonly injector: Injector,
    private readonly session: SessionService,
    private readonly feature: FeatureService,
    private readonly context: ContextService,
    private readonly aiFeaturesService: AiFeaturesService,
    private readonly configurationService: ConfigurationService,
    public readonly aiStore: AIStore,
    private readonly iqBoostStore: IQBoostViewStore,
    private readonly tracker: AiHubEventTracker,
    @Inject(CDN_URL) readonly cdn: string,
  ) {
    super(element);

    this.isFFAnonymizerEnabled.set(
      this.feature.isFeatureOn('core-ai-anonymizer-enabled', true),
    );

    this.addSubscription(
      this.saveUserData$
        .pipe(
          debounceTime(400),
          distinctUntilChanged(
            (a, b) => a.dataIsSecureExpanded === b.dataIsSecureExpanded,
          ),
          tap(() => this.loading.set(true)),
          switchMap((userData) =>
            this.saveAiHubUserData(userData).pipe(
              finalize(() => this.loading.set(false)),
            ),
          ),
        )
        .subscribe(),
    );

    effect(() => {
      const dataIsSecure = this.noteDataIsSecureExpanded();

      if (this.aiFeaturesGroup().length > 0 && this.hasInitialized) {
        this.saveUserData$.next({
          dataIsSecureExpanded: dataIsSecure,
        });
      }
    });
  }

  override doAction(name: string, args?: unknown): void {
    this.ngZone.run(() => {
      if (name === ActionEventType.Initialize) {
        args && this.initialize(args);
      } else {
        super.doAction(name, args);
      }
    });
  }

  private initialize(args: unknown): void {
    this.loading.set(true);
    this.tracker.trackEvent(EventJourney.Action, EventAction.Opened);

    forkJoin({
      labels: this.loadLabels(),
      session: this.session.getInfo(),
    })
      .pipe(
        tap(({ labels }) => (this._labels = labels)),
        switchMap(({ session }) => {
          this.aiHubUserDataSettingsCodes.code2 = session.user.id;
          this.iqBoostStore.configurationSettingCodesUser().code2 = session.user.id;
          this.isAdmin.set(session.user.roles.administrator);
          return forkJoin({
            config: this.aiFeaturesService.getConfiguration(session),
            userData: this.getAiHubUserData(),
            anonymizerConfig: this.aiFeaturesService.getAnonymizerConfiguration(),
          });
        }),
        tap(({ config, userData, anonymizerConfig }) => {
          this.noteDataIsSecureExpanded.set(userData.dataIsSecureExpanded);
          this.isAiEnabled.set(config.enabled);
          this.isAnonymizerEnabledByAdmin.set(anonymizerConfig.admin.anonymized);

          if (this.isAdmin()) {
            this.isAnonymizerEnabled.set(anonymizerConfig.admin.anonymized);
          } else {
            this.isAnonymizerEnabled.set(anonymizerConfig.user.anonymized || anonymizerConfig.admin.anonymized);
          }
          this.aiStore.setUserDataGroup(config.aiUserDataGroup);
          this.initializeFeatures();
          // Set hasInitialized to true after a small delay so it
          // won't trigger the save of config settings immediately.
          setTimeout(() => (this.hasInitialized = true), 1);
        }),
        finalize(() => this.loading.set(false)),
      )
      .subscribe();
  }

  private loadLabels(): Observable<Labels> {
    // Resolve here so translate runs after integration is in context — not in the constructor.
    const translate = this.injector.get(TranslateService);
    return translate.get(['aiHub']);
  }

  private initializeFeatures(): void {
    if (!this._labels) return;

    const userData = this.aiStore.aiUserDataGroup().user.aiFeaturesEnabled;
    const adminData = this.aiStore.aiUserDataGroup().admin.aiFeaturesEnabled;

    this.addFeature({
      enabled: userData.iqBoost && adminData.iqBoost,
      enabledAdmin: adminData.iqBoost,
      title: this._labels.aiHub.features.iqBoost.title,
      description: this.getVersionedContent(
        this._labels.aiHub.features.iqBoost.description,
      ),
      location: this.getVersionedContent(
        this._labels.aiHub.features.iqBoost.location,
      ),
      data: this.getVersionedContent(this._labels.aiHub.features.iqBoost.data),
      limitation: this._labels.aiHub.features.iqBoost.limitation,
      aiFeaturePropertyEnabled: 'iqBoost',
      app: {
        title: this._labels.aiHub.features.iqBoost.title,
        tag: 'maximizer-ai-iqboost',
        featureFlagId: 'core-ai-iq-boost-menu-shown',
      },
      featureFlagId: 'core-ai-iq-boost-shown',
      featureFlagDefault: false,
      imgSrc: `${this.cdn}/assets/images/previews/ai-hub/preview-iqboost-new.png`,
      iconClass: 'maximizer-icons icon-iq-boost icon-22 ml-2 mr-[-0.25rem]',
    });

    this.addFeature({
      enabled: userData.iqBoostWidget && adminData.iqBoostWidget,
      enabledAdmin: adminData.iqBoostWidget,
      title: this._labels.aiHub.features.iqBoostWidget.title,
      description: this.getVersionedContent(
        this._labels.aiHub.features.iqBoostWidget.description,
      ),
      location: this.getVersionedContent(
        this._labels.aiHub.features.iqBoostWidget.location,
      ),
      data: this.getVersionedContent(
        this._labels.aiHub.features.iqBoostWidget.data,
      ),
      limitation: this._labels.aiHub.features.iqBoostWidget.limitation,
      aiFeaturePropertyEnabled: 'iqBoostWidget',
      featureFlagId: 'core-ai-iq-boost-widget-shown',
      featureFlagDefault: false,
      imgSrc: `${this.cdn}/assets/images/previews/ai-hub/preview-iqboost-inline-summary.png`,
      iconClass:
        'maximizer-icons icon-inline-summary icon-22 ml-2 mr-[-0.25rem]',
    });

    this.addFeature({
      enabled: userData.summarization && adminData.summarization,
      enabledAdmin: adminData.summarization,
      title: this._labels.aiHub.features.summarization.title,
      description: this.getVersionedContent(
        this._labels.aiHub.features.summarization.description,
      ),
      location: this.getVersionedContent(
        this._labels.aiHub.features.summarization.location,
      ),
      data: this.getVersionedContent(
        this._labels.aiHub.features.summarization.data,
      ),
      limitation: this._labels.aiHub.features.summarization.limitation,
      aiFeaturePropertyEnabled: 'summarization',
      featureFlagId: 'core-timeline-ai-summarization-shown',
      featureFlagOrganizationId: 'core-ai-anonymizer-enabled',
      featureFlagDefault: false,
      imgSrc: `${this.cdn}/assets/images/previews/ai-hub/preview-timeline-summary-new.png`,
      iconClass:
        'maximizer-icons icon-timeline-summary icon-22 ml-2 mr-[-0.25rem]',
    });

    this.addFeature({
      enabled: userData.meetingPreparation && adminData.meetingPreparation,
      enabledAdmin: adminData.meetingPreparation,
      title: this._labels.aiHub.features.meetingPreparation.title,
      description: this.getVersionedContent(
        this._labels.aiHub.features.meetingPreparation.description,
      ),
      location: this.getVersionedContent(
        this._labels.aiHub.features.meetingPreparation.location,
      ),
      data: this.getVersionedContent(
        this._labels.aiHub.features.meetingPreparation.data,
      ),
      limitation: this._labels.aiHub.features.meetingPreparation.limitation,
      aiFeaturePropertyEnabled: 'meetingPreparation',
      featureFlagId: 'core-ai-meeting-preparation-summary-shown',
      featureFlagOrganizationId: 'core-ai-anonymizer-enabled',
      featureFlagDefault: false,
      imgSrc: `${this.cdn}/assets/images/previews/ai-hub/preview-meeting-preparation-new.png`,
      iconClass:
        'maximizer-icons icon-meeting-preparation icon-22 ml-2 mr-[-0.25rem]',
    });

    this.addFeature({
      enabled: userData.smartCapture && adminData.smartCapture,
      enabledAdmin: adminData.smartCapture,
      title: this._labels.aiHub.features.mobileSmartCapture.title,
      description: this.getVersionedContent(
        this._labels.aiHub.features.mobileSmartCapture.description,
      ),
      location: this.getVersionedContent(
        this._labels.aiHub.features.mobileSmartCapture.location,
      ),
      data: this.getVersionedContent(
        this._labels.aiHub.features.mobileSmartCapture.data,
      ),
      limitation: this._labels.aiHub.features.mobileSmartCapture.limitation,
      aiFeaturePropertyEnabled: 'smartCapture',
      featureFlagId: 'mobile-smartcapture-shown',
      featureFlagDefault: false,
      imgSrc: `${this.cdn}/assets/images/previews/ai-hub/preview-mobile-smart-capture.png`,
      iconClass:
        'maximizer-icons icon-smart-capture icon-22 ml-2 mr-[-0.25rem]',
    });

    this.addFeature({
      enabled: userData.outlook && adminData.outlook,
      enabledAdmin: adminData.outlook,
      title: this._labels.aiHub.features.outlook.title,
      description: this.getVersionedContent(
        this._labels.aiHub.features.outlook.description,
      ),
      location: this.getVersionedContent(
        this._labels.aiHub.features.outlook.location,
      ),
      data: this.getVersionedContent(this._labels.aiHub.features.outlook.data),
      limitation: this._labels.aiHub.features.outlook.limitation,
      aiFeaturePropertyEnabled: 'outlook',
      featureFlagId: 'integration-o365-ai-features-enabled',
      featureFlagDefault: false,
      imgSrc: `${this.cdn}/assets/images/previews/ai-hub/preview-outlook-autofill.png`,
      iconClass: 'fa-icons icon-envelope icon-20 ml-2',
    });

    // Temporarily disable these features
    const enableUtilityFeatures = false;

    if (enableUtilityFeatures) {
      this.addFeature(
        {
          enabled: true,
          enabledAdmin: true,
          title: this._labels.aiHub.features.microsoftCoPilot.title,
          description: this.getVersionedContent(
            this._labels.aiHub.features.microsoftCoPilot.description,
          ),
          location: '',
          data: this.getVersionedContent(
            this._labels.aiHub.features.microsoftCoPilot.data,
          ),
          limitation: this._labels.aiHub.features.microsoftCoPilot.limitation,
        },
        this._labels.aiHub.categories.utilities,
      );

      this.addFeature(
        {
          enabled: true,
          enabledAdmin: true,
          title: this._labels.aiHub.features.callTranscription.title,
          description: this.getVersionedContent(
            this._labels.aiHub.features.callTranscription.description,
          ),
          location: '',
          data: this.getVersionedContent(
            this._labels.aiHub.features.callTranscription.data,
          ),
          limitation: this._labels.aiHub.features.callTranscription.limitation,
        },
        this._labels.aiHub.categories.utilities,
      );

      this.addFeature(
        {
          enabled: true,
          enabledAdmin: true,
          title: this._labels.aiHub.features.docuscan.title,
          description: this.getVersionedContent(
            this._labels.aiHub.features.docuscan.description,
          ),
          location: '',
          data: this.getVersionedContent(
            this._labels.aiHub.features.docuscan.data,
          ),
          limitation: this._labels.aiHub.features.docuscan.limitation,
        },
        this._labels.aiHub.categories.utilities,
      );

      this.addFeature(
        {
          enabled: true,
          enabledAdmin: true,
          title: this._labels.aiHub.features.simplyAsk.title,
          description: this.getVersionedContent(
            this._labels.aiHub.features.simplyAsk.description,
          ),
          location: '',
          data: this.getVersionedContent(
            this._labels.aiHub.features.simplyAsk.data,
          ),
          limitation: this._labels.aiHub.features.simplyAsk.limitation,
        },
        this._labels.aiHub.categories.utilities,
      );
    }
  }

  private getVersionedContent(content: {
    faVersion: string;
    defaultVersion: string;
  }): string {
    return this.isSLE() ? content.defaultVersion : content.faVersion;
  }

  private addFeature(featureItem: AiFeatureItem, category?: string): void {
    if (
      featureItem.featureFlagId &&
      featureItem.featureFlagDefault !== undefined &&
      !this.feature.isFeatureOn(
        featureItem.featureFlagId,
        featureItem.featureFlagDefault,
      )
    ) {
      return;
    }

    let group = this.aiFeaturesGroup().find(
      (group) => group.category === category,
    );

    if (!group) {
      group = {
        category,
        items: [],
      };
      this.aiFeaturesGroup().push(group);
    }

    // If the App in the Feature Flag is not enabled, remove the App
    if (
      featureItem.app?.featureFlagId &&
      !this.feature.isFeatureOn(featureItem.app.featureFlagId, false)
    ) {
      featureItem.app = undefined;
    }

    group.items.push(featureItem);
  }

  openFeature(app: AiFeatureApp): void {
    const dialogArgs = new ShowDialogArgs(app.tag, '');
    if (app.args) {
      Object.assign(dialogArgs, app.args);
    }
    this.actionCompleted.emit(dialogArgs);
  }

  onFeatureToggle(feature: AiFeatureItem): void {
    if (feature.aiFeaturePropertyEnabled) {
      this.loading.set(true);

      if (this.aiStore.aiUserDataGroup().isAdmin) {
        this.aiStore.updateAiFeatureEnabledAdmin(
          feature.aiFeaturePropertyEnabled,
          feature.enabled,
        );
      }

      this.aiStore.updateAiFeatureEnabled(
        feature.aiFeaturePropertyEnabled,
        feature.enabled,
      );

      this.aiFeaturesService
        .setAIFeatures(this.aiStore.aiUserDataGroup().user.aiFeaturesEnabled)
        .pipe(
          tap((saved) => {
            if (saved && feature.aiFeaturePropertyEnabled) {
              this.tracker.trackEvent(
                EventJourney.Action,
                EventAction.Changed,
                {
                  mode: feature.enabled ? 'enabled' : 'disabled',
                  feature: feature.aiFeaturePropertyEnabled,
                },
              );
            }
          }),
          finalize(() => this.loading.set(false)),
        )
        .subscribe();
    }
  }

  isFeatureEnabled(feature: AiFeatureItem): boolean {
    return feature.enabledAdmin || this.aiStore.aiUserDataGroup().isAdmin;
  }

  isFeatureEnabledByOrganization(feature: AiFeatureItem): boolean {
    if (isNullOrEmpty(feature.featureFlagOrganizationId)) {
      return true;
    }
    return (
      !!feature.featureFlagOrganizationId &&
      this.feature.isFeatureOn(feature.featureFlagOrganizationId, false)
    );
  }

  setAiEnabled(enabled: boolean): void {
    this.loading.set(true);
    this.isAiEnabled.set(enabled);
    this.tracker.trackEvent(
      EventJourney.Action,
      enabled ? EventAction.Enabled : EventAction.Disabled,
    );

    this.addSubscription(
      this.aiFeaturesService
        .setEnabled(enabled)
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe(),
    );

    const launcher = top?.document?.getElementById(
      'ai-iqboost-launcher',
    ) as HTMLElement & EmbeddedComponent;
    launcher?.doAction(IQBoostInternalCommand.SetEnabled, enabled);
  }

  toggleTrustAndSafety(): void {
    this.noteDataIsSecureExpanded.update((expanded) => !expanded);
  }

  setAnonymizerEnabled(enabled: boolean): void {
    const previousAnonymizerEnabled = this.isAnonymizerEnabled();
    const previousAnonymizerEnabledByAdmin =
      this.isAnonymizerEnabledByAdmin();

    this.loading.set(true);
    this.isAnonymizerEnabled.set(enabled);
    if (this.isAdmin()) {
      this.isAnonymizerEnabledByAdmin.set(enabled);
    }

    this.addSubscription(
      this.aiFeaturesService
        .setAnonymizerEnabled(enabled, this.isAdmin())
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe((success) => {
          if (!success) {
            this.isAnonymizerEnabled.set(previousAnonymizerEnabled);
            if (this.isAdmin()) {
              this.isAnonymizerEnabledByAdmin.set(
                previousAnonymizerEnabledByAdmin,
              );
            }
            return;
          }

          this.iqBoostStore.setAnonymizedInAnimation({
            animate: true,
            anonymized: enabled,
          });
          setTimeout(() => {
            this.iqBoostStore.setAnonymizedInAnimation({
              animate: false,
              anonymized: enabled,
            });
          }, 1500);
        }),
    );
  }

  private getAiHubUserData(): Observable<AiHubUserData> {
    return this.configurationService
      .getSetting(this.aiHubUserDataSettingsCodes)
      .pipe(
        map((setting) => {
          this.aiHubUserDataSetting = setting;
          return setting?.textValue
            ? (JSON.parse(setting.textValue) as AiHubUserData)
            : AiHubComponent.aiHubUserDataDefault;
        }),
        catchError(() => of(AiHubComponent.aiHubUserDataDefault)),
      );
  }

  private saveAiHubUserData(userData: AiHubUserData): Observable<boolean> {
    const currentSetting = this.aiHubUserDataSetting
      ? of(this.aiHubUserDataSetting)
      : this.configurationService.getSetting(this.aiHubUserDataSettingsCodes);

    return currentSetting.pipe(
      switchMap((response) =>
        this.configurationService.updateSetting(
          response?.id ?? null,
          this.aiHubUserDataSettingsCodes,
          JSON.stringify(userData),
        ),
      ),
      tap((setting) => (this.aiHubUserDataSetting = setting)),
      catchError(() => of(null)),
      map((setting) => (setting?.id?.length ?? 0) > 0),
    );
  }

  override ngOnDestroy(): void {
    this.tracker.trackEvent(EventJourney.Action, EventAction.Closed);
    super.ngOnDestroy();
  }
}
